import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import UpcomingDrawsView from '../views/UpcomingDrawsView.vue'
import NextDrawsView from '../views/NextDrawsView.vue'
import WagersView from '../views/WagersView.vue'
import ResultsView from '../views/ResultsView.vue'
import HowToPlayView from '../views/HowToPlayView.vue'
import DetailView from '../views/DetailView.vue'
import TermsAndConditionsView from '../views/TermsAndConditionsView.vue'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { useAuthStore } from '@/stores/auth'
import { initializeTheme } from '@/composables/useTheme'
import { setLocale, SUPPORTED_LOCALES, DEFAULT_LOCALE, type SupportedLocale } from '@/i18n'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: (to) => ({ path: '/next-draws', query: to.query })
  },
  {
    path: '/all',
    name: 'all',
    component: UpcomingDrawsView,
    meta: { activeNav: 'all' }
  },
  {
    path: '/lottery/:slug',
    name: 'lottery-detail',
    component: DetailView,
    meta: { activeNav: 'all' }
  },
  {
    path: '/next-draws',
    name: 'next-draws',
    component: NextDrawsView,
    meta: { activeNav: 'next-draws' }
  },
  {
    path: '/wagers',
    name: 'wagers',
    component: WagersView,
    meta: { activeNav: 'wagers', requiresAuth: true }
  },
  {
    path: '/results',
    name: 'results',
    component: ResultsView,
    meta: { activeNav: 'results' }
  },
  {
    path: '/how-to-play',
    name: 'how-to-play',
    component: HowToPlayView,
    meta: { activeNav: 'how-to-play' }
  },
  {
    path: '/terms-and-conditions',
    name: 'terms-and-conditions',
    component: TermsAndConditionsView,
    meta: { activeNav: 'terms-and-conditions' }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' }
  }
})

router.beforeEach((to, from, next) => {
  const defaultParams = {
    locale: 'en',
    mode: 'light'
  }

  const mergedQuery = {
    ...defaultParams,
    ...from.query,
    ...to.query
  }

  // Check if theme should be preserved from previous route
  const shouldPreserveTheme = !!from.query.theme && !to.query.theme
  
  const needsRedirect =
    !to.query.locale || !to.query.mode || shouldPreserveTheme

  const authStore = useAuthStore()
  
  // URL query params take precedence, but preserve session for internal navigation
  const hasUrlToken = !!to.query.token
  const hasUrlAccount = !!to.query.account
  const hasSessionToken = sessionStorage.getItem('auth_token')
  const hasSessionAccount = sessionStorage.getItem('auth_account')
  
  if (hasUrlToken) {
    const cleanToken = (to.query.token as string).split('?')[0]
    sessionStorage.setItem('auth_token', cleanToken)
    authStore.token = cleanToken
  } else if (hasSessionToken && !authStore.token) {
    // No URL token but session exists - restore from session
    authStore.token = hasSessionToken
  }
  
  if (hasUrlAccount) {
    const cleanAccount = (to.query.account as string).split('?')[0]
    sessionStorage.setItem('auth_account', cleanAccount)
    authStore.account = cleanAccount
  } else if (hasSessionAccount && !authStore.account) {
    // No URL account but session exists - restore from session
    authStore.account = hasSessionAccount
  }
  
  if (to.query.currency) {
    const cleanCurrency = (to.query.currency as string).split('?')[0]
    sessionStorage.setItem('auth_currency', cleanCurrency)
  }

  if (to.meta.requiresAuth) {
    const hasSessionToken = !!sessionStorage.getItem('auth_token')
    const hasSessionAccount = !!sessionStorage.getItem('auth_account')
    const hasUrlToken = !!to.query.token
    const hasUrlAccount = !!to.query.account
    
    if (!hasUrlToken && !hasUrlAccount && !hasSessionToken && !hasSessionAccount) {
      console.warn('Authentication parameters (token, account) are required for this route')
    }
  }

  if (needsRedirect) {
    next({ ...to, query: mergedQuery })
  } else {
    next()
  }
})

router.afterEach(async (to, from) => {
  // Only re-initialize theme if account or theme query param changed
  const accountChanged = to.query.account !== from.query.account
  const themeChanged = to.query.theme !== from.query.theme
  
  if (accountChanged || themeChanged) {
    try {
      await initializeTheme(true)
    } catch (error) {
      console.error('Failed to re-initialize theme:', error)
    }
  }

  // Sync locale with i18n when locale query param changes
  const localeParam = (to.query.locale as string) || (to.query.lang as string)
  if (localeParam && SUPPORTED_LOCALES.includes(localeParam as SupportedLocale)) {
    const localeChanged = localeParam !== from.query.locale && localeParam !== from.query.lang
    if (localeChanged || !from.name) {
      try {
        await setLocale(localeParam as SupportedLocale)
      } catch (error) {
        console.error('Failed to set locale:', error)
      }
    }
  }
  
  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendRouteChange(to.path, to.meta.title as string || document.title)
  }
})

export default router
